import React, { useCallback, useMemo, type ReactNode } from 'react';
import { useCrossFlow, Targets } from '@atlassiansox/cross-flow-support';
import Lozenge from '@atlaskit/lozenge';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAnalyticsEvents,
	fireTrackAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import {
	type MenuNode,
	MenuNodeType,
} from '@atlassian/jira-software-context-menu/src/common/types.tsx';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import { useBacklogSelector } from '../../../../../../state/index.tsx';
import { getProjectName, projectIdSelector } from '../../../../../../state/selectors/app/index.tsx';
import { getCards } from '../../../../../../state/selectors/cards/index.tsx';
import { useCoUseFlow } from '../../../../../whiteboards-entrypoint-experiment/co-use-flow/index.tsx';
import messages from '../../../../../whiteboards-entrypoint-experiment/messages.tsx';
import { onClickCTA } from '../../../../../whiteboards-entrypoint-experiment/on-click-cta/utils.tsx';
import { PLAN_ON_WHITEBOARD_MENU_ID } from '../../constants.tsx';
import type { Props, ContentProps } from './types.tsx';

const usePlanOnWhiteboardContent = ({
	issueKey,
	selectedCardIds,
	cardEntities,
	flow,
}: ContentProps): MenuNode[] => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const crossflow = useCrossFlow();
	const { fireInteractedUIEvent } = useFireCrossFlowAnalyticsEvents();

	const coUseEligible = flow === 'coUse';
	const crossFlowEligible = flow === 'crossFlow';

	const url = useCoUseFlow(coUseEligible);

	// Extract project name and project id
	const projectName = useBacklogSelector(getProjectName);
	const projectId = useBacklogSelector(projectIdSelector);

	const planOnWhiteboardButtonLabel = useMemo(
		(): ReactNode => (
			<>
				{`${formatMessage(messages.planOnWhiteboard)} `}
				{crossFlowEligible && (
					<Lozenge appearance="new">{formatMessage(messages.planOnWhiteboardTry)}</Lozenge>
				)}
			</>
		),
		[formatMessage, crossFlowEligible],
	);

	const saveSelectedIssuesToLocalStorage = useCallback(
		(issueKeys: string[]) => {
			localStorage.setItem('whiteboardsExperimentSelectedIssueKeys', JSON.stringify(issueKeys));
			fireTrackAnalytics(createAnalyticsEvent({}), 'selectedIssuesInLocalStorage stored', {
				growthInitiative: 'cross-flow',
				storedIssues: issueKeys,
				sourceComponent: 'planOnWhiteboardsContextualCTAScreen',
				sourceContext: 'planOnWhiteboardContextMenuButton',
			});
		},
		[createAnalyticsEvent],
	);

	const onPlanOnWhiteboard = useCallback(() => {
		const selectedIssueKeys = selectedCardIds
			.map((id: unknown) => cardEntities[String(id)]?.key ?? null)
			.filter(Boolean);
		selectedIssueKeys.indexOf(issueKey) === -1 && selectedIssueKeys.push(issueKey);
		saveSelectedIssuesToLocalStorage(selectedIssueKeys);

		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'dropdownItem',
			}),
			'planOnWhiteboardContextMenuButton',
			{
				growthInitiative: 'cross-flow',
				numSelectedCards: selectedCardIds.length,
				isCrossFlowEligible: crossFlowEligible,
				isCoUseEligible: coUseEligible,
				sourceComponent: 'planOnWhiteboardsContextualCTAScreen',
				sourceContext: 'planOnWhiteboardContextMenuButton',
			},
		);

		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'listItem',
				actionSubjectId: 'planOnWhiteboardContextMenuButton',
			},
			{
				targetProductId: Targets.CONFLUENCE,
				recommendedProductIds: [Targets.CONFLUENCE],
				source: 'planOnWhiteboardsContextualCTAScreen',
			},
		);

		onClickCTA({
			createAnalyticsEvent,
			crossFlowEligible,
			crossflow,
			projectId,
			projectName,
			component: 'planOnWhiteboardsContextualCTAScreen',
			context: 'planOnWhiteboardContextMenuButton',
			coUseEligible,
			url,
		});
	}, [
		selectedCardIds,
		cardEntities,
		crossFlowEligible,
		coUseEligible,
		projectId,
		projectName,
		crossflow,
		createAnalyticsEvent,
		fireInteractedUIEvent,
		saveSelectedIssuesToLocalStorage,
		issueKey,
		url,
	]);

	return useMemo(
		() => [
			{
				type: MenuNodeType.Item,
				label: () => planOnWhiteboardButtonLabel,
				id: PLAN_ON_WHITEBOARD_MENU_ID,
				onClick: onPlanOnWhiteboard,
			},
		],
		[planOnWhiteboardButtonLabel, onPlanOnWhiteboard],
	);
};

export const usePlanOnWhiteboard = ({ issueKey, selectedCardIds, flow }: Props) => {
	const cardEntities = useBacklogSelector(getCards);

	return usePlanOnWhiteboardContent({
		issueKey,
		selectedCardIds,
		cardEntities,
		flow,
	});
};
